/**
 * 导航栏面包屑组件
 * 根据当前路径和菜单数据动态生成面包屑导航
 */

import React, { useMemo } from 'react';
import { Breadcrumb } from 'antd';
import { useLocation } from 'react-router-dom';
import { useMenuDataSimple } from '../../../../hooks/useMenuDataSimple';
import { MenuItem } from '../../../../types/menu';

interface BreadcrumbNavProps {
  className?: string;
}

const BreadcrumbNav: React.FC<BreadcrumbNavProps> = ({ className }) => {
  const location = useLocation();
  const { menuData, loading, buildMenuTree } = useMenuDataSimple();

  // 根据当前路径查找菜单项路径（包含父级菜单）
  // 根据菜单数据的树形结构实时组装面包屑导航
  const breadcrumbItems = useMemo(() => {
    const currentPath = location.pathname;
    
    // 生成路径后备面包屑的辅助函数
    const generatePathBasedBreadcrumb = () => {
      const pathSegments = currentPath.split('/').filter(segment => segment);
      if (pathSegments.length === 0) {
        return [{ title: '首页' }];
      }
      
      // 系统管理相关的路径（这些应该在"系统管理"下）
      const systemManagementPaths = ['user', 'role', 'menu', 'dict', 'log', 'permissions'];
      
      // 业务数据相关的路径（这些应该在"业务数据"下）
      const businessDataPaths = ['platform', 'institution', 'news', 'showcase', 'business', 'policy', 'common-config', 'carousel'];
      
      const items: Array<{ title: string }> = [];
      
      // 根据路径段生成面包屑
      pathSegments.forEach((segment) => {
        let title = segment;
        let parentTitle = '';
        
        // 判断是否是系统管理相关的路径
        if (systemManagementPaths.includes(segment)) {
          parentTitle = '系统管理';
          // 映射子菜单名称
          if (segment === 'user') {
            title = '用户管理';
          } else if (segment === 'role') {
            title = '角色管理';
          } else if (segment === 'menu') {
            title = '菜单管理';
          } else if (segment === 'dict') {
            title = '数据字典';
          } else if (segment === 'log') {
            title = '系统日志';
          } else if (segment === 'permissions') {
            title = '权限管理';
          }
          
          // 如果父级菜单还没添加，先添加父级
          if (items.length === 0 || items[items.length - 1].title !== parentTitle) {
            items.push({ title: parentTitle });
          }
          items.push({ title });
        } else if (businessDataPaths.includes(segment)) {
          parentTitle = '业务数据';
          // 映射子菜单名称
          if (segment === 'platform') {
            title = '平台简介管理';
          } else if (segment === 'institution') {
            title = '入驻机构管理';
          } else if (segment === 'news') {
            title = '新闻管理';
          } else if (segment === 'showcase') {
            title = '精品展示管理';
          } else if (segment === 'business') {
            title = '业务指南管理';
          } else if (segment === 'policy') {
            title = '政策导航管理';
          } else if (segment === 'common-config') {
            title = '通用配置';
          } else if (segment === 'carousel') {
            title = '轮播图管理';
          }
          
          // 如果父级菜单还没添加，先添加父级
          if (items.length === 0 || items[items.length - 1].title !== parentTitle) {
            items.push({ title: parentTitle });
          }
          items.push({ title });
        } else if (segment === 'data') {
          // 'data' 是父路径，不需要单独显示
          // 只在没有其他路径段时显示
          if (pathSegments.length === 1) {
            items.push({ title: '数据维护' });
          }
        } else if (segment === 'home' || segment === 'main') {
          // 'home' 和 'main' 都表示首页
          items.push({ title: '首页' });
        } else {
          // 其他未知路径段，直接显示
          items.push({ title });
        }
      });
      
      // 如果生成了面包屑项，返回；否则至少显示首页
      return items.length > 0 ? items : [{ title: '首页' }];
    };
    
    // 如果菜单数据还在加载中，先使用路径生成基本面包屑（不等待菜单数据）
    // 如果菜单数据已经加载过（从缓存或全局变量），即使为空也尝试匹配
    if (loading && (!menuData || menuData.length === 0)) {
      return generatePathBasedBreadcrumb();
    }
    
    // 如果菜单数据为空，使用路径生成基本面包屑
    if (!menuData || menuData.length === 0) {
      return generatePathBasedBreadcrumb();
    }

    // 构建菜单树形结构（包含父子关系）
    const menuTree = buildMenuTree(menuData);

    // 递归查找菜单路径（根据菜单树形结构实时组装）
    const findMenuPath = (
      items: MenuItem[],
      targetPath: string,
      path: MenuItem[] = []
    ): MenuItem[] | null => {
      for (const item of items) {
        const itemPath = item.path || '';
        const newPath = [...path, item];
        
        // 标准化路径进行比较（去掉尾部斜杠）
        const normalizePath = (p: string) => p.replace(/\/$/, '') || '/';
        const normalizedItemPath = normalizePath(itemPath);
        const normalizedTargetPath = normalizePath(targetPath);
        
        // 检查路径是否匹配（完全匹配）
        const isExactMatch = normalizedItemPath === normalizedTargetPath;
        
        // 前缀匹配：当前路径以菜单路径开头（用于匹配父级菜单）
        // 例如：菜单路径是 /data，当前路径是 /data/institution
        const isPrefixMatch = normalizedItemPath && 
                             normalizedItemPath !== '/' && 
                             normalizedTargetPath.startsWith(normalizedItemPath + '/');
        
        // 部分匹配：提取路径的最后一部分进行比较（用于处理路径格式不一致的情况）
        // 例如：菜单路径可能是 /institution，当前路径是 /data/institution
        const getLastPathSegment = (p: string) => {
          const segments = p.split('/').filter(s => s);
          return segments.length > 0 ? segments[segments.length - 1] : '';
        };
        const itemLastSegment = getLastPathSegment(normalizedItemPath);
        const targetLastSegment = getLastPathSegment(normalizedTargetPath);
        const isSegmentMatch = itemLastSegment && targetLastSegment && 
                              itemLastSegment === targetLastSegment &&
                              normalizedItemPath; // 确保菜单路径不为空
        
        // 如果当前项路径完全匹配，返回包含父级菜单的完整路径
        if (isExactMatch && normalizedItemPath) {
          return newPath;
        }
        
        // 如果是前缀匹配（父级菜单），继续查找子菜单，但不立即返回
        // 只在子菜单中找到匹配时才返回包含父级的路径
        
        // 检查子菜单（递归查找）
        if (item.children && item.children.length > 0) {
          const result = findMenuPath(item.children, targetPath, newPath);
          if (result) {
            // 如果子菜单匹配，返回包含父级菜单的完整路径
            return result;
          }
        }
        
        // 如果没有子菜单，但当前路径的最后段与菜单路径的最后段匹配，也返回
        // 这用于处理菜单路径格式不一致的情况
        if (!item.children || item.children.length === 0) {
          if (isSegmentMatch) {
            return newPath;
          }
        }
      }
      return null;
    };

    const menuPath = findMenuPath(menuTree, currentPath);

    if (menuPath && menuPath.length > 0) {
      // 构建面包屑：根据菜单树形结构实时组装，包含所有父级菜单和当前菜单
      // 过滤掉空的菜单项，保留有名称的菜单
      const breadcrumbItems = menuPath
        .filter(item => item && item.menuName)
        .map((item, index) => {
          const isLast = index === menuPath.length - 1;
          return {
            title: item.menuName,
            // 如果不是最后一项，可以添加点击跳转功能（可选）
            // path: item.path,
          };
        });
      
      if (breadcrumbItems.length > 0) {
        return breadcrumbItems;
      }
    }

    // 如果找不到匹配的菜单，使用路径生成基本的面包屑导航
    // 这样可以确保即使菜单数据为空或找不到匹配，也能显示导航信息
    return generatePathBasedBreadcrumb();
  }, [menuData, location.pathname, buildMenuTree, loading]);

  // 确保始终有面包屑项，即使为空也显示默认项
  const displayItems = breadcrumbItems && breadcrumbItems.length > 0 
    ? breadcrumbItems 
    : [{ title: '首页' }];

  // 始终渲染面包屑组件，确保可见性
  if (!displayItems || displayItems.length === 0) {
    // 如果还是没有项，至少显示首页
    return (
      <Breadcrumb 
        items={[{ title: '首页' }]}
        className={className}
      />
    );
  }

  return (
    <Breadcrumb 
      items={displayItems}
      className={className}
    />
  );
};

export default BreadcrumbNav;

